/**
 * WC-07: Challenge Hub Page
 *
 * Main page for the Weekly Challenge system showing all days and progress.
 * Updated: Auth timing fix applied
 */

import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useChallengeStore,
  DayStatus,
} from '../stores/useChallengeStore';
import {
  DayChallengeCard,
  WeekHeader,
  StreakDisplay,
  RewardsPanel,
} from '../components/challenges';
import { useAuth } from '../contexts/AuthContext';
import { PageAtmosphere } from '../components/atmosphere';
import { NineSliceBorder } from '../components/atmosphere/NineSliceBorder';
import '../styles/atmosphere/pages/create.css';
import './ChallengeHubPage.css';

export const ChallengeHubPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const {
    currentWeek,
    progress,
    isLoading,
    isLoadingProgress,
    error,
    fetchCurrentWeek,
    fetchProgress,
    selectDay,
    clearError,
  } = useChallengeStore();

  // Fetch challenge data on mount (no auth required)
  useEffect(() => {
    fetchCurrentWeek();
  }, [fetchCurrentWeek]);

  // Fetch user progress only after auth is ready
  useEffect(() => {
    // Wait for auth to be fully initialized
    if (authLoading || !user) {
      return;
    }

    // Small delay to ensure __getAuthToken is set by AuthContext's useEffect
    const timer = setTimeout(() => {
      fetchProgress();
    }, 100);

    return () => clearTimeout(timer);
  }, [authLoading, user, fetchProgress]);

  // Calculate derived stats
  const stats = useMemo(() => {
    if (!progress) {
      return {
        daysCompleted: 0,
        totalStars: 0,
        currentStreak: 0,
      };
    }

    const daysCompleted = Object.values(progress.days).filter(
      (d) => d.status === 'completed'
    ).length;

    return {
      daysCompleted,
      totalStars: progress.totalStars,
      currentStreak: progress.currentStreak,
    };
  }, [progress]);

  // Get day status from progress
  const getDayStatus = (day: number): DayStatus => {
    if (!progress) return day === 1 ? 'available' : 'locked';
    return progress.days[day]?.status || 'locked';
  };

  // Get day progress
  const getDayProgress = (day: number) => {
    if (!progress) return undefined;
    return progress.days[day];
  };

  // Handle day selection
  const handleStartDay = (day: number) => {
    selectDay(day);
    navigate(`/challenges/day/${day}`);
  };

  const handleViewDetails = (day: number) => {
    selectDay(day);
    navigate(`/challenges/day/${day}`);
  };

  // Loading state - show while loading challenge or waiting for auth
  if ((isLoading && !currentWeek) || authLoading) {
    return (
      <PageAtmosphere page="challenge">
        <div className="challenge-hub-page">
          <div className="loading-container">
            <div className="loading-spinner" />
            <p>{authLoading ? 'Authenticating...' : 'Loading challenges...'}</p>
          </div>
        </div>
      </PageAtmosphere>
    );
  }

  // Error state
  if (error) {
    return (
      <PageAtmosphere page="challenge">
        <div className="challenge-hub-page">
          <div className="error-container">
            <h2>Error Loading Challenges</h2>
            <p>{error}</p>
            <button onClick={() => { clearError(); fetchCurrentWeek(); }}>
              Try Again
            </button>
          </div>
        </div>
      </PageAtmosphere>
    );
  }

  // No challenge available
  if (!currentWeek) {
    return (
      <PageAtmosphere page="challenge">
        <div className="challenge-hub-page">
          <div className="no-challenge-container">
            <h2>No Active Challenge</h2>
            <p>Check back soon for the next weekly challenge!</p>
          </div>
        </div>
      </PageAtmosphere>
    );
  }

  return (
    <PageAtmosphere page="challenge">
    <div className="challenge-hub-page">
      {/* Challenge Banner */}
      <div className="challenge-banner">
        <img
          src="/assets/challengepage/volcanic-depths-banner.jpeg"
          alt={`${currentWeek.theme} Challenge`}
          className="challenge-banner-img"
        />
      </div>

      <div className="challenge-hub-content">
        {/* Week Header */}
        <WeekHeader
          weekId={currentWeek.weekId}
          theme={currentWeek.theme}
          narrative={currentWeek.narrative}
        />

        {/* Progress Stats */}
        <StreakDisplay
          currentStreak={stats.currentStreak}
          daysCompleted={stats.daysCompleted}
          totalDays={7}
          totalStars={stats.totalStars}
          maxStars={21}
        />

        {/* Day Cards Grid */}
        <div className="days-grid">
          {(currentWeek.days || []).map((day) => {
            const status = getDayStatus(day.day);
            const dayProgress = getDayProgress(day.day);

            return (
              <DayChallengeCard
                key={day.day}
                day={day.day}
                name={day.name}
                mode={day.mode}
                flavor={day.flavor}
                status={status}
                attempts={dayProgress?.attempts}
                bestResult={dayProgress?.bestResult}
                enemies={(day.enemies || []).map((e) => ({
                  name: e.name,
                  difficulty: e.difficulty,
                  count: e.count,
                }))}
                onStart={() => handleStartDay(day.day)}
                onViewDetails={() => handleViewDetails(day.day)}
              />
            );
          })}
        </div>

        {/* Rewards Panel */}
        <RewardsPanel rewards={currentWeek.rewards} progress={progress} />
      </div>
    </div>
    </PageAtmosphere>
  );
};
